<template>
    <v-form ref="form"
            v-model="valid"
            lazy-validation>
        <h2 v-if="title"
            class="headline font-weight-bold"
            :class="{ 'mb-4': !description }">
            {{ $vuetify.lang.t(title) }}
        </h2>
        <p v-if="description"
           class="body-1">
            {{ $vuetify.lang.t(description) }}
        </p>

        <v-row dense>
            <template v-for="(formItem, formItemIndex) in formItems">
                <v-col :cols="formItem.width ? formItem.width : 12"
                       class="pt-0 pb-0"
                       :class="formItem.classes ? formItem.classes : ''"
                       :key="formItemIndex">

                    <!-- hint -->
                    <div v-if="formItem.content && formItem.is === 'hint'" class="body-1 error--text mb-4">
                        <div class="font-weight-bold text-uppercase">
                            {{ $vuetify.lang.t(`$vuetify.hints.hint`) }}
                        </div>
                        {{ $vuetify.lang.t(`$vuetify.hints.${formItem.content}`) }}
                    </div>

                    <!-- phoneinput -->
                    <phone-input v-if="formItem.is === 'phone'"
                                 filled
                                 dense
                                 :name="formItem.name"
                                 v-model="form[formItem.name]"
                                 mode="international"
                                 :onlyCountries="countryCodes"
                                 default-country="DE"
                                 valid-characters-only
                                 :rules="applyRules(rules, formItem)"
                                 :text-field-label="formItem.label ? $vuetify.lang.t(formItem.label) : $vuetify.lang.t(`$vuetify.form_items.${formItem.name}`)"
                                 required></phone-input>

                    <!-- autocomplete -->
                    <v-autocomplete v-if="formItem.is === 'autocomplete' && !formItem.disabled"
                                    required
                                    :items="formItem.options"
                                    :item-text="itemText"
                                    :item-value="itemValue"
                                    v-model="form[formItem.name]"
                                    :rules="formItem.rule ? rules[formItem.rule] : rules[formItem.name]"
                                    dense
                                    filled
                                    :label="formItem.label ? $vuetify.lang.t(formItem.label) : $vuetify.lang.t(`$vuetify.form_items.${formItem.name}`)"
                                    :hint="formItem.hint ? $vuetify.lang.t(formItem.hint) : ''"
                                    :placeholder="formItem.placeholder ? $vuetify.lang.t(formItem.placeholder) : ''"
                    ></v-autocomplete>

                    <!-- select -->
                    <v-select v-if="formItem.is === 'select' && !formItem.disabled"
                              filled
                              dense
                              :label="formItem.label ? $vuetify.lang.t(formItem.label) : $vuetify.lang.t(`$vuetify.form_items.${formItem.name}`)"
                              :hint="formItem.hint ? $vuetify.lang.t(formItem.hint) : ''"
                              :placeholder="formItem.placeholder ? $vuetify.lang.t(formItem.placeholder) : ''"
                              :items="formItem.options"
                              :item-text="itemText"
                              :item-value="itemValue"
                              v-model="form[formItem.name]"
                              :rules="formItem.rule ? rules[formItem.rule] : rules[formItem.name]"
                              required></v-select>

                    <!-- text -->
                    <v-text-field v-if="formItem.is === 'text'"
                                  filled
                                  dense
                                  :name="formItem.name"
                                  :label="formItem.label ? $vuetify.lang.t(formItem.label) : $vuetify.lang.t(`$vuetify.form_items.${formItem.name}`)"
                                  :hint="formItem.hint ? $vuetify.lang.t(formItem.hint) : ''"
                                  :placeholder="formItem.placeholder ? $vuetify.lang.t(formItem.placeholder) : ''"
                                  :rules="applyRules(rules, formItem)"
                                  v-model="form[formItem.name]"
                                  required></v-text-field>

                    <!-- textarea -->
                    <v-textarea v-if="formItem.is === 'textarea'"
                                filled
                                dense
                                :name="formItem.name"
                                :label="formItem.label ? $vuetify.lang.t(formItem.label) : $vuetify.lang.t(`$vuetify.form_items.${formItem.name}`)"
                                :hint="formItem.hint ? $vuetify.lang.t(formItem.hint) : ''"
                                :placeholder="formItem.placeholder ? $vuetify.lang.t(formItem.placeholder) : ''"
                                :rules="applyRules(rules, formItem)"
                                v-model="form[formItem.name]"
                                required></v-textarea>

                    <!-- password -->
                    <v-text-field v-if="formItem.is === 'password'"
                                  filled
                                  dense
                                  counter
                                  :name="formItem.name"
                                  :label="formItem.label ? $vuetify.lang.t(formItem.label) : $vuetify.lang.t(`$vuetify.form_items.${formItem.name}`)"
                                  :hint="formItem.hint ? $vuetify.lang.t(formItem.hint) : ''"
                                  :rules="formItem.rule ? rules[formItem.rule] : rules[formItem.name]"
                                  v-model="form[formItem.name]"
                                  autocomplete="new-password"
                                  :type="!showPassword.includes(formItem.name) ? 'text' : 'password'"
                                  :append-icon="!showPassword.includes(formItem.name) ? 'mdi-eye' : 'mdi-eye-off'"
                                  @click:append="toggleShowPassword(formItem.name)"
                                  required></v-text-field>

                    <!-- checkboxes -->
                    <v-checkbox v-if="formItem.is === 'checkbox'"
                                v-model="form[formItem.name]"
                                color="primary"
                                :readonly="formItem.readonly"
                                dense
                                :rules="formItem.rule ? rules[formItem.rule] : rules[formItem.name]">
                        <template v-slot:label>
                            <div v-if="formItem.link || formItem.slug && formItem.linkName && formItem.label" class="white--text">
                                <i18n :path="formItem.label">

                                    <template v-slot:0>
                                        <v-tooltip bottom v-if="formItem.tooltip">
                                            <template v-slot:activator="{ on }">
                                                <template v-if="formItem.link">
                                                    <a target="_blank"
                                                       :href="formItem.link"
                                                       @click.stop
                                                       v-on="on">{{ $vuetify.lang.t(formItem.linkName) }}</a>
                                                </template>
                                                <template v-if="formItem.slug">
                                                    <static-content-dialog :slug="formItem.slug">
                                                        <a href="#" @click.prevent>{{ $vuetify.lang.t(formItem.linkName) }}</a>
                                                    </static-content-dialog>
                                                </template>
                                            </template>
                                            {{ $vuetify.lang.t(formItem.tooltip) }}
                                        </v-tooltip>

                                        <template v-else>
                                            <template v-if="formItem.link">
                                                <a target="_blank"
                                                   :href="formItem.link"
                                                   @click.stop>{{ $vuetify.lang.t(formItem.linkName) }}</a>
                                            </template>
                                            <template v-if="formItem.slug">
                                                <a href="#" @click.prevent>{{ $vuetify.lang.t(formItem.linkName) }}</a>
                                            </template>
                                        </template>
                                    </template>

                                    <template v-slot:1 v-if="formItem.linkName2 && formItem.slug2">
                                        <v-tooltip bottom v-if="formItem.tooltip">
                                            <template v-slot:activator="{ on }">
                                                <template v-if="formItem.link">
                                                    <a target="_blank"
                                                       :href="formItem.link"
                                                       @click.stop
                                                       v-on="on">{{ $vuetify.lang.t(formItem.linkName2) }}</a>
                                                </template>
                                                <template v-if="formItem.slug2">
                                                    <static-content-dialog :slug="formItem.slug2">
                                                        <a href="#" @click.prevent>{{ $vuetify.lang.t(formItem.linkName2) }}</a>
                                                    </static-content-dialog>
                                                </template>
                                            </template>
                                            {{ $vuetify.lang.t(formItem.tooltip) }}
                                        </v-tooltip>

                                        <template v-else>
                                            <template v-if="formItem.link">
                                                <a target="_blank"
                                                   :href="formItem.link"
                                                   @click.stop>{{ $vuetify.lang.t(formItem.linkName2) }}</a>
                                            </template>
                                            <template v-if="formItem.slug2">
                                                <a href="#" @click.prevent>{{ $vuetify.lang.t(formItem.linkName2) }}</a>
                                            </template>
                                        </template>
                                    </template>
                                </i18n>
                            </div>
                            <template v-else>
                                {{ formItem.label ? $vuetify.lang.t(formItem.label) : $vuetify.lang.t(`$vuetify.form_items.${formItem.name}`) }}
                            </template>
                        </template>
                    </v-checkbox>

                    <!-- date -->
                    <v-dialog v-if="formItem.is === 'date'"
                              :ref="`dateDialog${formItemIndex}`"
                              :return-value.sync="form[formItem.name]"
                              persistent
                              width="290px"
                              max-width="100%">
                        <template v-slot:activator="{ on }">
                            <v-text-field :value="form[formItem.name] | date"
                                          :label="formItem.label ? $vuetify.lang.t(formItem.label) : $vuetify.lang.t(`$vuetify.form_items.${formItem.name}`)"
                                          :rules="formItem.rule ? rules[formItem.rule] : rules[formItem.name]"
                                          readonly
                                          filled
                                          v-on="on"></v-text-field>
                        </template>
                        <v-date-picker v-model="form[formItem.name]"
                                       scrollable
                                       header-color="primary darken-1"
                                       color="white accent"
                                       full-width
                                       :locale="$i18n.locale"
                                       :max="getValidDateLimit">
                            <v-spacer></v-spacer>
                            <v-btn text color="primary" @click="$refs[`dateDialog${formItemIndex}`][0].isActive = false">
                                {{ $vuetify.lang.t('$vuetify.buttons.cancel') }}
                            </v-btn>
                            <v-btn text color="primary" @click="$refs[`dateDialog${formItemIndex}`][0].save(form[formItem.name])">
                                {{ $vuetify.lang.t('$vuetify.buttons.ok') }}
                            </v-btn>
                        </v-date-picker>
                    </v-dialog>

                    <!-- subheadline -->
                    <h3 v-if="formItem.is === 'subhead'" class="subtitle-1 font-weight-bold" :class="formItem.classes" v-html="$vuetify.lang.t(formItem.label)"></h3>

                </v-col>
            </template>

        </v-row>

        <div class="d-flex justify-space-between">
            <template v-for="(action, actionIndex) in actions">
                <!-- previous and next -->
                <v-btn v-if="action.is === 'previous'"
                       class="mr-auto"
                       color="primary"
                       icon
                       @click.native="previous">
                    <v-icon>mdi-arrow-left-circle-outline</v-icon>
                </v-btn>
                <v-btn v-if="action.is === 'next'"
                       class="ml-auto"
                       color="primary"
                       icon
                       :disabled="!valid || $wait.waiting(loaderKey)"
                       @click="next">
                    <v-icon>mdi-arrow-right-circle-outline</v-icon>
                </v-btn>

                <!-- button -->
                <v-btn v-if="action.is === 'button'"
                       :class="action.classes ? action.classes : 'ml-auto'"
                       :color="action.color"
                       :disabled="(!valid && !action.noValidation) || ($wait.waiting(loaderKey) && !action.noValidation)"
                       :loading="$wait.waiting(loaderKey)"
                       @click="method(action)">
                    {{ $vuetify.lang.t(`$vuetify.buttons.${action.name}`) }}
                </v-btn>
            </template>
        </div>
    </v-form>
</template>
<script>
    // 3rd
    import { mapState } from 'vuex';
    // components
    import PhoneInput from '../PhoneInput';
    import StaticContentDialog from '../StaticContentDialog';

    export default {
        name: 'build-form',
        components: {
            PhoneInput,
            StaticContentDialog,
        },
        props: {
            loaderKey: {
                default: 'submit-register',
            },
            itemText: {
                default: 'text',
            },
            itemValue: {
                default: 'value',
            },
            title: {},
            description: {},
            formItems: {},
            actions: {},
            rules: {},
            form: {},

            validateBeforeCallAction: {
                default: true,
            },

            nextStep: {},
            previousStep: {},
        },
        data() {
            return {
                valid: true,
                showPassword: [],
            }
        },
        computed: {
            ...mapState([
                'countryCodes',
            ]),
            getValidDateLimit() {
                const date = new Date();
                date.setFullYear(date.getFullYear() - 18);
                const month = String((date.getMonth() + 1)).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${date.getFullYear()}-${month}-${day}`;
            },
        },
        methods: {
            toggleShowPassword(formItemName) {
                const index = this.showPassword.indexOf(formItemName);
                if (index === -1) {
                    this.showPassword.push(formItemName);
                } else {
                    this.showPassword.splice(index, 1);
                }
            },
            applyRules(rules, currentFormItem) {
                let skipValidation = false;
                if (currentFormItem.optionalBy) {
                    const optionalBy = currentFormItem.optionalBy.split(':');
                    const key = optionalBy[0];
                    const value = optionalBy[1];
                    this.formItems.forEach((formItem) => {
                        if (formItem.name === key && this.form[formItem.name] === value) skipValidation = true;
                    });
                }
                if (skipValidation) {
                    return [];
                }

                return currentFormItem.rule ? rules[currentFormItem.rule] : rules[currentFormItem.name];
            },
            previous() {
                this.previousStep();
            },
            next() {
                if (this.validateBeforeCallAction && !this.$refs.form.validate()) return false;
                this.nextStep();
            },
            method(action) {
                if (this.validateBeforeCallAction && !this.$refs.form.validate() && !action.noValidation) return false;
                action.clickMethod();
            },
        },
        beforeMount() {
            // prepare showPassword array if form is containing password fields
            this.formItems.forEach(formItem => (formItem.is === 'password') ? this.showPassword.push(formItem.name) : null);
        },
    }
</script>
